import PropTypes from 'prop-types';
import React from 'react';
import { NotificationManager } from 'react-notifications';
import { FormGroup, Label } from 'reactstrap';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import { ListGroup, ListGroupEmptyItem } from '@ami/react/core';
import { SortableListGroupItem } from '@ami/react/core';
import { formatPrice } from './quotes/priceFormatter';
import { Input } from '@ami/react/core';
import { SubLineItem } from './subLineItem';

const AddSubLineItem = ({
  description,
  dNetPrice,
  onSave,
  onUpdate,
  isEdit,
  onCancelEdit,
}) => (
  <React.Fragment>
    <div className="form-row form-group">
      <div className={`col-${isEdit ? 6 : 8}`}>
        <Input
          value={description}
          placeholder="Enter a new description"
          onChange={(e) => onUpdate({ description: e.target.value })}
          onKeyPress={(e) => {
            if (e.charCode === 13) {
              e.preventDefault();
              onSave();
            }
          }}
        />
      </div>
      <div className="col-3">
        <Input
          prependValue="$"
          type="number"
          label="D. NET"
          value={dNetPrice}
          onChange={(e) => onUpdate({ dNetPrice: e.target.value })}
        />
      </div>
      <div className={`col-${isEdit ? 3 : 1} text-center`}>
        <button type="button" className="btn btn-link ps-1" onClick={onSave}>
          {isEdit ? 'save' : 'add'}
        </button>
        {isEdit && (
          <button
            type="button"
            className="btn btn-link ps-1"
            onClick={onCancelEdit}
          >
            cancel
          </button>
        )}
      </div>
    </div>
  </React.Fragment>
);

class Component extends React.Component {
  static propTypes = {
    lineItem: PropTypes.PropTypes.shape({
      subLineItems: PropTypes.array.isRequired,
    }).isRequired,
    onUpdate: PropTypes.func.isRequired,
  };

  state = {
    selectedSubLineItemTempId: null,
    description: '',
    dNetPrice: 0,
  };

  removeItem = (item) => {
    this.props.onUpdate(
      this.props.lineItem.subLineItems.filter(
        (x) => x.description !== item.description
      )
    );
  };

  moveCard = (dragIndex, hoverIndex) => {
    const { subLineItems } = this.props.lineItem;

    const movedItem = subLineItems.find((item, index) => index === dragIndex);
    const remainingItems = subLineItems.filter(
      (item, index) => index !== dragIndex
    );

    this.props.onUpdate([
      ...remainingItems.slice(0, hoverIndex),
      movedItem,
      ...remainingItems.slice(hoverIndex),
    ]);
  };

  saveItem = () => {
    if (!this.state.description) {
      return NotificationManager.error(
        'Description is required for sub line item'
      );
    }

    const { subLineItems } = this.props.lineItem;
    if (
      subLineItems.find(
        (x) =>
          x.description === this.state.description &&
          x.tempId !== this.state.selectedSubLineItemTempId
      )
    ) {
      return NotificationManager.warning(
        'Sub line item has been added already'
      );
    }

    if (this.state.selectedSubLineItemTempId != null) {
      const subLineItem = subLineItems.find(
        (x) => x.tempId === this.state.selectedSubLineItemTempId
      );
      if (subLineItem) {
        subLineItem.description = this.state.description;
        subLineItem.price = parseFloat(this.state.dNetPrice);
      } else {
        NotificationManager.error(
          'There was an error editing the sub line item'
        );
      }
      this.props.onUpdate(subLineItems);
    } else {
      this.props.onUpdate([
        ...subLineItems,
        new SubLineItem({
          price: parseFloat(this.state.dNetPrice),
          description: this.state.description,
        }),
      ]);
    }

    this.cancelEdit();
  };

  startEditingItem = (item) => {
    const selectedSubLineItemTempId = Math.random();
    item.tempId = selectedSubLineItemTempId;
    this.setState({
      selectedSubLineItemTempId,
      description: item.description,
      dNetPrice: item.price,
    });
  };

  cancelEdit = () => {
    this.setState({
      selectedSubLineItemTempId: null,
      description: '',
      dNetPrice: 0,
    });
  };

  render() {
    this.props.lineItem.subLineItems.forEach((subLineItem) => {
      subLineItem.tempDragId = Math.random();
    });
    return (
      <FormGroup>
        <Label className="form-label">Sub Line Items</Label>
        <ListGroup>
          {this.props.lineItem.subLineItems.map((x, i) => (
            <SortableListGroupItem
              heading={x.description}
              details={formatPrice(x.price)}
              onRemove={() => this.removeItem(x)}
              onEditClicked={() => this.startEditingItem(x)}
              isEditingActive={this.state.selectedSubLineItemTempId != null}
              id={x.tempDragId}
              index={i}
              moveCard={this.moveCard}
              key={x.description}
            />
          ))}
          {this.props.lineItem.subLineItems.length === 0 && (
            <ListGroupEmptyItem>
              No sub line items are configured
            </ListGroupEmptyItem>
          )}
        </ListGroup>
        <AddSubLineItem
          description={this.state.description}
          dNetPrice={this.state.dNetPrice}
          onSave={this.saveItem}
          onUpdate={(updateObject) => this.setState(updateObject)}
          onCancelEdit={this.cancelEdit}
          isEdit={this.state.selectedSubLineItemTempId != null}
        />
      </FormGroup>
    );
  }
}

const SubLineItemsFormComponent = DragDropContext(HTML5Backend)(Component);
export { SubLineItemsFormComponent };
